// The building template: Lumber Mill / Quarry / Iron Mine (production), Warehouse (storage), Farm (population), Hiding Place
// (hidden stock) and Walls (defense). Hero → the live numbers as cards → the upgrade card with the one crimson CTA.
import { useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { api } from "@/api/client";
import type { Village } from "@/api/types";
import { art } from "@/kit/art";
import { Countdown, LevelTrack, NowNext, ScreenHero } from "@/kit/city";
import { Bar, Button, Card, CardHead, Medallion, Row, RowText, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { BUILDING_BLURB, BUILDING_NAMES, RESOURCE_KEYS, RESOURCE_NAMES, type BuildingKey, type ResourceKey } from "@/game/constants";
import { fmtNum, abbrev } from "@/game/format";
import { fmtDuration, fmtSpan, fmtWhen } from "@/game/time";
import { WALL_FLAT, WALL_PCT, buildingSpecial } from "@/game/rules";
import {
  buildingCost, buildingMap, capacity, constructionQueue, effectExpiry, fullInMs, isMaxed, nextLevel, nowNext, populationBreakdown, prereqList, queueSlots, shortfall, storeItem, tableNamed,
} from "@/game/city";
import { CityPage, useCity } from "./CityRoute";
import { IconHammer } from "@/kit/icons";

const TEMPLATE_KEYS: BuildingKey[] = ["b_w", "b_c", "b_i", "b_wh", "b_f", "b_h", "b_wa"];
const RES_OF: Partial<Record<BuildingKey, ResourceKey>> = { b_w: "r_w", b_c: "r_c", b_i: "r_i" };
const HERO: Record<string, { art: string; cutout?: boolean; width?: string }> = {
  b_w: { art: art.hero.lumberMill }, b_wh: { art: art.hero.warehouse }, b_f: { art: art.hero.farm },
  b_c: { art: art.hero.quarry, cutout: true }, b_i: { art: art.hero.ironMine, cutout: true }, b_h: { art: art.hero.hidingPlace, cutout: true }, b_wa: { art: art.hero.walls, cutout: true },
};
const hudIcon = (r: ResourceKey) => art.hud[r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"];
const resName = (r: ResourceKey) => RESOURCE_NAMES[r].toLowerCase();

export function Building() {
  const { key } = useParams();
  const { v, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  const k = key as BuildingKey;
  const b = v ? buildingMap(v)[k] : undefined;
  const queue = useMemo(() => (v ? constructionQueue(v) : []), [v]);
  if (!TEMPLATE_KEYS.includes(k)) return <Navigate to={base} replace />;
  if (!v || !b) return <CityPage>{null}</CityPage>;

  const res = RES_OF[k];
  const level = b.level;
  const queued = queue.filter((q) => q.item.key === k);
  const hero = HERO[k];
  const stats = heroStats(v, k, now);

  return (
    <CityPage>
      <div className="stack">
        <ScreenHero level={level} title={BUILDING_NAMES[k]} blurb={BUILDING_BLURB[k]} art={hero.art} cutout={hero.cutout} artWidth={hero.width} onBack={() => nav(base)} onGear={() => nav(`${base}/hall`)} gearLabel="City overview" stats={stats} />

        {res && <ProductionCards v={v} k={k} r={res} level={level} max={b.max} now={now} />}
        {k === "b_wh" && <WarehouseCards v={v} level={level} max={b.max} />}
        {k === "b_f" && <FarmCards v={v} level={level} max={b.max} />}
        {k === "b_h" && <HidingCards v={v} level={level} max={b.max} />}
        {k === "b_wa" && <WallCards v={v} level={level} max={b.max} />}

        <UpgradeCard v={v} k={k} queued={queued} now={now} />
      </div>
    </CityPage>
  );
}

// ------------------------------------------------------------------ hero stats per building

function heroStats(v: Village, k: BuildingKey, now: number) {
  const b = buildingMap(v)[k];
  const lvl = { label: "Level", value: b.level, small: `of ${b.max}` };
  const res = RES_OF[k];
  if (res) {
    const boost = effectExpiry(v, `boost_${res === "r_w" ? "wood" : res === "r_c" ? "clay" : "iron"}`);
    return [
      lvl,
      { label: RESOURCE_NAMES[res], value: fmtNum(v.resources_production?.[res]), small: "/ hr" },
      boost && boost > now ? { label: "Boost", value: "+25%", tone: "green" as const, small: <Countdown endMs={boost} compact /> } : { label: "Boost", value: "Off", tone: "crim" as const },
    ];
  }
  if (k === "b_wh") {
    const cap = capacity(v);
    const fullest = RESOURCE_KEYS.map((r) => ({ r, pct: cap ? (v.resources?.[r] ?? 0) / cap : 0 })).sort((a, b2) => b2.pct - a.pct)[0];
    return [lvl, { label: "Capacity", value: fmtNum(cap), small: "each" }, { label: "Fullest", value: `${Math.round(fullest.pct * 100)}%`, small: resName(fullest.r) }];
  }
  if (k === "b_f") {
    return [lvl, { label: "Population", value: fmtNum(v.current_population), small: `/ ${fmtNum(v.max_population)}` }, { label: "Free", value: fmtNum(Math.max(0, v.max_population - v.current_population)), tone: "green" as const }];
  }
  if (k === "b_h") {
    const cap = v.building_details?.b_h?.current_capacity ?? buildingSpecial("b_h", b.level) ?? 0;
    return [lvl, { label: "Hidden", value: fmtNum(cap), small: "each" }, { label: "Safe now", value: fmtNum(RESOURCE_KEYS.reduce((a, r) => a + Math.min(cap, v.resources?.[r] ?? 0), 0)), small: "in all" }];
  }
  // walls
  const flat = WALL_FLAT[Math.min(25, b.level)] ?? 20;
  const pct = Math.round((WALL_PCT[Math.min(25, b.level)] ?? 0) * 100);
  return [lvl, { label: "Basic defense", value: fmtNum(flat) }, { label: "Defense bonus", value: `+${pct}%`, tone: "green" as const }];
}

// ------------------------------------------------------------------ production buildings

function ProductionCards({ v, k, r, level, max, now }: { v: Village; k: BuildingKey; r: ResourceKey; level: number; max: number; now: number }) {
  const t = v.building_details?.[k]?.tables?.[0];
  const nn = nowNext(t);
  const nowRate = t?.current_production ?? nn.now ?? v.resources_production?.[r] ?? 0;
  const nextRate = t?.next_production ?? nn.next;
  const maxed = level >= max;
  const liveRate = v.resources_production?.[r] ?? nowRate;
  const full = fullInMs(v, r);
  const boostKey = `boost_${r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"}`;
  const boostItem = storeItem(v, boostKey);
  const boostEnd = effectExpiry(v, boostKey);
  const active = !!boostEnd && boostEnd > now;
  const name = RESOURCE_NAMES[r];
  const { wid, vid, actions } = useCity();
  return (
    <>
      <Card>
        <CardHead title={`${name} production`} aside="per hour" />
        <NowNext
          nowLabel={`Now · Level ${level}`}
          nextLabel={maxed ? "Final level" : `Next · Level ${level + 1}`}
          now={fmtNum(nowRate)}
          next={nextRate !== null ? fmtNum(nextRate) : "—"}
          maxed={maxed}
          delta={nextRate !== null && nextRate > nowRate ? <>+{fmtNum(nextRate - nowRate)} / hr (+{nowRate ? Math.round(((nextRate - nowRate) / nowRate) * 100) : 0}%)</> : undefined}
          nextNote={nextRate !== null && nextRate <= nowRate && active ? "before the boost" : undefined}
        />
        <LevelTrack level={level} max={max} />
      </Card>
      <Card>
        <CardHead title="Output" />
        <div className="rows c-kvs">
          <Row><Medallion src={hudIcon(r)} alt="" size={34} style={{ border: 0, background: "transparent", boxShadow: "none" }} /><RowText line="Per day" /><div className="right"><div className="val"><b>{fmtNum(liveRate * 24)}</b></div></div></Row>
          <Row><RowText line={<>Warehouse full in <span className="muted" style={{ fontStyle: "italic", fontSize: "var(--fs-meta)" }}>{abbrev(v.resources?.[r])} of {abbrev(capacity(v))}</span></>} /><div className="right"><div className="val"><b>{full === 0 ? "Full" : Number.isFinite(full) ? fmtSpan(full) : "—"}</b></div></div></Row>
        </div>
      </Card>
      {boostItem && (
        <Card>
          <div style={{ display: "flex", gap: "calc(var(--u) * 18)", alignItems: "flex-start" }}>
            <Medallion src={art.store[boostKey]} alt="" size={84} glow={active ? "ember" : undefined} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ margin: 0, font: "600 var(--fs-h)/1.1 var(--title)", letterSpacing: ".11em", textTransform: "uppercase", color: "var(--g-text)", display: "flex", alignItems: "center", gap: "calc(var(--u) * 12)" }}>
                Production boost {active ? <span className="c-active">Active <small>· <Countdown endMs={boostEnd!} compact /></small></span> : <span style={{ font: "700 var(--fs-label)/1 var(--caps)", textTransform: "var(--caps-tt)" as never, letterSpacing: ".14em", color: "var(--crim-ink)", border: "1px solid var(--crim-mid)", borderRadius: "999px", padding: "4px 10px" }}>Off</span>}
              </h2>
              <p style={{ margin: "calc(var(--u) * 10) 0 0", font: "400 var(--fs-body2)/1.4 var(--text)", color: "var(--cream)" }}>
                {boostItem.name}: +25% for 24 hours — <b className="gold">{fmtNum((active ? liveRate / 1.25 : liveRate) * 1.25)} / hr</b> while active.{active ? " Buying again adds 24 hours." : ""}
              </p>
              <Button kind="gold" wide style={{ marginTop: "calc(var(--u) * 14)" }} busy={actions.isBusy(`buy:${boostKey}`)} disabled={(v.gold?.amount_owned ?? 0) < boostItem.cost}
                onClick={() => actions.run(`buy:${boostKey}`, () => api.store.buy({ wid, vid }, boostKey, 1), { success: `${name} boosted for 24 hours.` })}>
                {active ? "Extend 24 hours" : "Boost 24 hours"} <span className="sep" /> <span className="coin" /> {boostItem.cost}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}

// ------------------------------------------------------------------ warehouse

function WarehouseCards({ v, level, max }: { v: Village; level: number; max: number }) {
  const nn = nowNext(tableNamed(v, "b_wh", "Storage Capacity"));
  const cap = capacity(v);
  const next = v.building_details?.b_wh?.next_capacity ?? nn.next ?? buildingSpecial("b_wh", level + 1);
  const maxed = level >= max;
  return (
    <>
      <Card>
        <CardHead title="Storage capacity" aside="per resource" />
        <NowNext nowLabel={`Now · Level ${level}`} nextLabel={`Next · Level ${level + 1}`} now={fmtNum(cap)} next={next ? fmtNum(next) : "—"} maxed={maxed}
          delta={next ? <>+{fmtNum(next - cap)} (+{cap ? Math.round(((next - cap) / cap) * 100) : 0}%)</> : undefined} />
        <LevelTrack level={level} max={max} />
      </Card>
      <Card>
        <CardHead title="In storage" aside={`of ${fmtNum(cap)}`} />
        <div className="stack" style={{ gap: "calc(var(--u) * 16)" }}>
          {RESOURCE_KEYS.map((r) => {
            const n = v.resources?.[r] ?? 0;
            const pct = cap ? n / cap : 0;
            return (
              <div key={r} style={{ display: "grid", gridTemplateColumns: "calc(var(--u) * 150) 1fr calc(var(--u) * 150)", alignItems: "center", gap: "calc(var(--u) * 16)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "calc(var(--u) * 10)" }}><img src={hudIcon(r)} alt="" style={{ width: "calc(var(--u) * 34)", height: "calc(var(--u) * 34)", objectFit: "contain" }} />{RESOURCE_NAMES[r]}</span>
                <Bar value={pct} color={pct >= 0.95 ? "crim" : "gold"} />
                <span style={{ textAlign: "right" }}><b className="creamt">{abbrev(n)}</b> <span className="muted" style={{ fontSize: "var(--fs-meta)" }}>{Math.round(pct * 100)}%</span></span>
              </div>
            );
          })}
        </div>
        <div className="c-note left">Food is population, not stock — see the Farm.</div>
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ farm

function FarmCards({ v, level, max }: { v: Village; level: number; max: number }) {
  const br = populationBreakdown(v);
  const used = v.current_population ?? 0;
  const cap = v.max_population ?? 0;
  const free = Math.max(0, cap - used);
  const nn = nowNext(tableNamed(v, "b_f", "Max Population"));
  const next = v.building_details?.b_f?.next_level_max_population ?? nn.next ?? buildingSpecial("b_f", level + 1);
  const maxed = level >= max;
  const seg = (n: number, c: string) => <i key={c} style={{ width: `${cap ? (n / cap) * 100 : 0}%`, background: c }} />;
  return (
    <>
      <Card>
        <CardHead title="Population" aside={`${cap ? Math.round((used / cap) * 100) : 0}% used`} />
        <div className="popbar" aria-label={`${fmtNum(used)} of ${fmtNum(cap)} population in use`}>
          {br ? [seg(br.buildings, "#6E5432"), seg(br.buildingsQueued, "#8A6733"), seg(br.troops, "#C9A24D"), seg(br.troopsQueued, "#EDD69D")] : seg(used, "#C9A24D")}
          <span className="free">{fmtNum(free)} free</span>
        </div>
        {br && (
          <div className="rows c-kvs" style={{ marginTop: "calc(var(--u) * 14)" }}>
            {[["Buildings", br.buildings, "#6E5432"], ["Buildings in production", br.buildingsQueued, "#8A6733"], ["Troops", br.troops, "#C9A24D"], ["Troops in production", br.troopsQueued, "#EDD69D"]].map(([l, n, c]) => (
              <Row key={String(l)}><span className="swatch" style={{ background: String(c) }} /><RowText line={String(l)} /><div className="right"><div className="val"><b>{fmtNum(Number(n))}</b></div></div></Row>
            ))}
          </div>
        )}
        <div className="card-foot"><span className="eyebrow">In use</span><span><b className="creamt" style={{ font: "600 var(--fs-value)/1 var(--text)" }}>{fmtNum(used)}</b> <span className="muted">of {fmtNum(cap)}</span></span></div>
      </Card>
      <Card>
        <CardHead title="Max population" />
        <NowNext nowLabel={`Now · Level ${level}`} nextLabel={`Next · Level ${level + 1}`} now={fmtNum(cap)} next={next ? fmtNum(next) : "—"} maxed={maxed}
          delta={next ? <>+{fmtNum(next - cap)} (+{cap ? Math.round(((next - cap) / cap) * 100) : 0}%)</> : undefined} />
        <LevelTrack level={level} max={max} />
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ hiding place

function HidingCards({ v, level, max }: { v: Village; level: number; max: number }) {
  const nn = nowNext(tableNamed(v, "b_h", "Storage Capacity"));
  const cap = v.building_details?.b_h?.current_capacity ?? nn.now ?? buildingSpecial("b_h", level) ?? 0;
  const next = nn.next ?? buildingSpecial("b_h", level + 1);
  const maxed = level >= max;
  const hiddenTable = tableNamed(v, "b_h", "Hidden Resources");
  const hidden = (r: ResourceKey) => {
    const row = hiddenTable?.table.find((x) => RESOURCE_NAMES[r] in x);
    return row ? Number(Object.values(row)[0]) : Math.min(cap, v.resources?.[r] ?? 0);
  };
  return (
    <>
      <Card>
        <CardHead title="Hidden per resource" aside="safe from raiders" />
        <NowNext nowLabel={`Now · Level ${level}`} nextLabel={`Next · Level ${level + 1}`} now={fmtNum(cap)} next={next ? fmtNum(next) : "—"} maxed={maxed}
          delta={next ? <>+{fmtNum(next - cap)} (+{cap ? Math.round(((next - cap) / cap) * 100) : 0}%)</> : undefined} />
        <LevelTrack level={level} max={max} />
      </Card>
      <Card>
        <CardHead title="Hidden now" aside={`of ${fmtNum(cap)} each`} />
        <div className="rows c-kvs">
          {RESOURCE_KEYS.map((r) => (
            <Row key={r}><img src={hudIcon(r)} alt="" style={{ width: "calc(var(--u) * 34)", height: "calc(var(--u) * 34)", objectFit: "contain" }} /><RowText line={RESOURCE_NAMES[r]} meta={`${abbrev(v.resources?.[r])} in stock`} /><div className="right"><div className="val"><b>{fmtNum(hidden(r))}</b> <span className="muted">hidden</span></div></div></Row>
          ))}
        </div>
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ walls

function WallCards({ v, level, max }: { v: Village; level: number; max: number }) {
  const flatNow = WALL_FLAT[Math.min(25, level)] ?? 20;
  const flatNext = WALL_FLAT[Math.min(25, level + 1)] ?? flatNow;
  const pctNow = Math.round((WALL_PCT[Math.min(25, level)] ?? 0) * 100);
  const pctNext = Math.round((WALL_PCT[Math.min(25, level + 1)] ?? 0) * 100);
  const maxed = level >= max;
  void v;
  return (
    <>
      <Card>
        <CardHead title="Basic defense" aside="added to every fight" />
        <NowNext nowLabel={`Now · Level ${level}`} nextLabel={`Next · Level ${level + 1}`} now={fmtNum(flatNow)} next={fmtNum(flatNext)} maxed={maxed} delta={<>+{fmtNum(flatNext - flatNow)}</>} />
        <LevelTrack level={level} max={max} />
      </Card>
      <Card>
        <CardHead title="Defensive bonus" aside="on all defenders" />
        <NowNext nowLabel={`Now · Level ${level}`} nextLabel={`Next · Level ${level + 1}`} now={`+${pctNow}%`} next={`+${pctNext}%`} maxed={maxed} delta={<>+{pctNext - pctNow} points</>} />
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ the upgrade card

/** The next level's cost and what stands in the way. The button puts the next level straight into the construction queue
 *  (Ben, Oct 10 2026); the city view's construction mode is still there for building from the map of the city. */
function UpgradeCard({ v, k, queued, now }: { v: Village; k: BuildingKey; queued: ReturnType<typeof constructionQueue>; now: number }) {
  const { wid, vid, actions } = useCity();
  const b = buildingMap(v)[k];
  const cost = buildingCost(b);
  const s = shortfall(v, cost);
  const maxed = isMaxed(b);
  const next = nextLevel(b);
  const queue = constructionQueue(v);
  const full = queue.length >= queueSlots(v);
  const prereqs = prereqList(b.prereqs_string);
  const shortRes = Object.entries(s.short) as [ResourceKey, number][];
  let note: React.ReactNode = null;
  let reason: string | null = null;
  if (maxed) { note = <>Final level reached.</>; reason = "max"; }
  else if (!b.has_prereqs) reason = prereqs.length ? `Requires ${prereqs.join(", ")}` : "Requirements not met";
  else if (full) reason = `Queue full — a slot opens in ${fmtDuration(Math.max(0, queue[0].endMs - now))}`;
  else if (s.pop) reason = `Needs ${fmtNum(cost.p_u ?? 0)} free population — upgrade the Farm`;
  else if (shortRes.length) {
    const [r, n] = shortRes.sort((a, b2) => b2[1] - a[1])[0];
    const rate = v.resources_production?.[r] ?? 0;
    reason = `Enough ${resName(r)} in about ${Number.isFinite(s.waitMs) ? fmtSpan(s.waitMs) : "—"} at ${fmtNum(rate)} / hr`;
    void n;
  }
  if (!reason && next === b.max) note = <>Final level{k === "b_wh" ? ` · capacity ${fmtNum(buildingSpecial("b_wh", b.max) ?? 0)} each` : ""}</>;
  return (
    <Card>
      <CardHead title={maxed ? "Fully upgraded" : `Upgrade to level ${next}`} aside={<>paid from <b className="gold">{v.village?.city_name}</b></>} />
      {!maxed && (
        <div className="c-grid3">
          {RESOURCE_KEYS.map((r) => {
            const short = s.short[r];
            return (
              <div key={r} className={cx("c-tile", short && "shortfall")} style={{ textAlign: "left", display: "flex", gap: "calc(var(--u) * 10)", alignItems: "center", padding: "calc(var(--u) * 14) calc(var(--u) * 12)" }}>
                <img src={hudIcon(r)} alt={RESOURCE_NAMES[r]} style={{ width: "calc(var(--u) * 40)", height: "calc(var(--u) * 40)", objectFit: "contain" }} />
                <div style={{ minWidth: 0 }}>
                  <div className={cx("val", short && "crimtxt")} style={{ marginTop: 0, font: "600 var(--fs-value)/1 var(--text)", fontSize: "calc(var(--fs-value) * .95)" }}>{fmtNum(cost[r])}</div>
                  <div className="muted" style={{ font: "italic 400 var(--fs-meta)/1.2 var(--text)", marginTop: 4, color: short ? "var(--crim-ink)" : undefined }}>{short ? `need ${fmtNum(short)} more` : "in stock"}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {queued.length > 0 && <div className="c-note">Level {queued[queued.length - 1].item.level} is in the queue · done at {fmtWhen(queued[queued.length - 1].endMs, now)}</div>}
      {(note || (reason && reason !== "max")) && <div className="c-note" style={{ color: reason && reason !== "max" ? "var(--cream)" : undefined }}>{reason && reason !== "max" ? reason : note}</div>}
      {/* inside a building the upgrade goes straight into the construction queue (Ben, Oct 10 2026) — no trip to
          construction mode, which stays the way to build from the city view */}
      {!maxed && (
        <Button kind="crimson" wide style={{ marginTop: "calc(var(--u) * 12)" }} disabled={!!reason} busy={actions.isBusy(`up-${k}`)}
          onClick={() => void actions.run(`up-${k}`, () => api.village.expand({ wid, vid: vid! }, k), { success: `${BUILDING_NAMES[k]} level ${next} added to the queue.` })}>
          <IconHammer /> {b.level > 0 || queued.length > 0 ? `Upgrade to level ${next}` : "Build level 1"}
        </Button>
      )}
      {!maxed && !reason && cost.time ? <div className="c-note">Takes {fmtDuration(cost.time)}{cost.p_u ? ` · ${fmtNum(cost.p_u)} population` : ""}</div> : null}
    </Card>
  );
}
